import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Snackbar,
  Skeleton,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import useSmqPermissions from "../../../hooks/useSmqPermissions";
import SMQMainContainer from "../../../components/SMQMainContainer";
import ProcessusService from "../../../services/smq/ProcessusService";
import HeaderProcessus from "./HeaderProcessus";
import ProcessusAdd from "./ProcessusAdd";

const CATEGORY_THEMES = {
  controle: {
    background: "rgba(113, 55, 223, 0.025)",
    color: "#7637eb",
    selectedText: "#fff",
  },
  direction: {
    background: "rgba(23, 61, 97, 0.018)",
    color: "#173d61",
    selectedText: "#fff",
  },
  realisation: {
    background: "rgba(25, 114, 62, 0.025)",
    color: "#14713b",
    selectedText: "#fff",
  },
  support: {
    background: "rgba(198, 86, 8, 0.025)",
    color: "#c65305",
    selectedText: "#fff",
  },
};

const DEFAULT_THEME = {
  background: "#f3f6f9",
  color: "#52677c",
  selectedText: "#fff",
};

const normalizeText = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("fr");

const getCategoryTheme = (categoryName) => {
  const normalizedName = normalizeText(categoryName);
  const themeName = Object.keys(CATEGORY_THEMES).find((name) =>
    normalizedName.includes(name)
  );

  return CATEGORY_THEMES[themeName] || DEFAULT_THEME;
};

const getCategoryTitle = (categoryName) => {
  const name = String(categoryName || "sans catégorie")
    .replace(/^processus\s+(de\s+)?/i, "")
    .trim();

  return "Processus de " + name;
};

const getSafeOrder = (value) => {
  if (value === null || value === undefined || value === "") {
    return Number.MAX_SAFE_INTEGER;
  }

  const order = Number(value);
  return Number.isFinite(order) ? order : Number.MAX_SAFE_INTEGER;
};

const sortCategories = (categories) =>
  categories
    .map((category, index) => ({ category, index }))
    .sort(
      (left, right) =>
        getSafeOrder(left.category.categorie_ordre) -
          getSafeOrder(right.category.categorie_ordre) ||
        left.index - right.index
    )
    .map(({ category }) => category);

const CategoryCard = memo(
  ({ addDisabled, canManage, category, onAdd, onSelect, selectedProcessId }) => {
    const theme = getCategoryTheme(category.categorie_name);
    const processus = Array.isArray(category.processus)
      ? category.processus
      : [];

    return (
      <Paper
        aria-label={
          "Processus de la catégorie " +
          (category.categorie_name || "sans catégorie")
        }
        component="div"
        elevation={0}
        role="region"
        sx={{
          backgroundColor: theme.background,
          border: "2px solid " + theme.color,
          boxSizing: "border-box",
          borderRadius: "7px",
          minHeight: 94,
          px: { xs: 1.25, sm: 1.5 },
          py: 1.25,
        }}
      >
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            gap: 1,
            justifyContent: "space-between",
            mb: 1.25,
          }}
        >
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              gap: 0.9,
              minWidth: 0,
            }}
          >
            <Box
              aria-hidden="true"
              sx={{
                backgroundColor: theme.color,
                borderRadius: "50%",
                boxShadow: "0 0 0 3px " + theme.background,
                flexShrink: 0,
                height: 10,
                width: 10,
              }}
            />
            <Typography
              component="h2"
              sx={{
                color: theme.color,
                fontSize: { xs: 10, sm: 11 },
                fontWeight: 800,
                letterSpacing: "0.09em",
                lineHeight: 1.3,
                textTransform: "uppercase",
              }}
            >
              {getCategoryTitle(category.categorie_name)}
            </Typography>
          </Box>

          {canManage && <Button
            aria-label={
              "Ajouter un processus à la catégorie " +
              (category.categorie_name || "sans catégorie")
            }
            color="inherit"
            disabled={addDisabled}
            onClick={() => onAdd(category)}
            size="small"
            sx={{
              color: "#10263d",
              flexShrink: 0,
              fontSize: 11,
              fontWeight: 700,
              minWidth: "auto",
              px: 0.75,
              textTransform: "none",
            }}
            type="button"
          >
            + Ajouter
          </Button>}
        </Box>

        {processus.length > 0 ? (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.8,
              minWidth: 0,
              overflowX: "auto",
              pb: 0.15,
            }}
          >
            {processus.map((process) => {
              const isSelected =
                String(selectedProcessId) === String(process.processus_id);

              return (
                <Chip
                  aria-pressed={isSelected}
                  clickable
                  key={process.processus_id}
                  label={
                    <Box
                      component="span"
                      sx={{
                        alignItems: "center",
                        display: "inline-flex",
                        gap: 0.7,
                        maxWidth: "100%",
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontWeight: 700,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {process.referentiel_nom}
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          backgroundColor: isSelected
                            ? "rgba(255, 255, 255, 0.2)"
                            : theme.color + "14",
                          borderRadius: "999px",
                          flexShrink: 0,
                          fontFamily: "'DM Mono', monospace",
                          fontSize: 9,
                          fontWeight: 700,
                          px: 0.65,
                          py: 0.15,
                        }}
                      >
                        {process.instance}
                      </Box>
                    </Box>
                  }
                  onClick={() => onSelect(process.processus_id)}
                  sx={{
                    backgroundColor: isSelected ? theme.color : "transparent",
                    borderColor: theme.color,
                    color: isSelected ? theme.selectedText : theme.color,
                    fontSize: 11,
                    height: 28,
                    maxWidth: { xs: "100%", sm: 380 },
                    "&:focus-visible": {
                      outline: "3px solid " + theme.color + "40",
                      outlineOffset: "2px",
                    },
                    "&:hover": {
                      backgroundColor: isSelected
                        ? theme.color
                        : theme.color + "12",
                    },
                    "& .MuiChip-label": {
                      display: "block",
                      minWidth: 0,
                      px: 1.15,
                    },
                  }}
                  title={`${process.instance || ""} — ${process.referentiel_nom || ""} (${process.direction_code || ""})`}
                  variant={isSelected ? "filled" : "outlined"}
                />
              );
            })}
          </Box>
        ) : (
          <Typography color="#60758a" fontSize={11} fontStyle="italic">
            Aucun processus dans cette catégorie.
          </Typography>
        )}
      </Paper>
    );
  }
);

CategoryCard.displayName = "CategoryCard";

const SideLabel = ({
  children,
  placement = "top",
  reverse = false,
}) => (
  <Box
    aria-hidden="true"
    sx={{
      alignItems: "center",
      alignSelf: "stretch",
      display: { xs: "none", md: "flex" },
      justifyContent: placement === "bottom" ? "flex-end" : "flex-start",
      position: "relative",
      py: 0.5,
    }}
  >
    <Box
      sx={{
        backgroundColor: "#b9c8d8",
        bottom: 0,
        left: "50%",
        position: "absolute",
        top: 0,
        transform: "translateX(-50%)",
        width: "1px",
      }}
    />
    <Box
      component="span"
      sx={{
        backgroundColor: "#edf2f8",
        color: "#52677c",
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: "0.04em",
        lineHeight: 1,
        position: "relative",
        py: 0.9,
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        writingMode: "vertical-rl",
        zIndex: 1,
        ...(reverse && { transform: "rotate(180deg)" }),
      }}
    >
      {children}
    </Box>
  </Box>
);

const LoadingCartography = () => (
  <Box aria-label="Chargement de la cartographie" aria-live="polite">
    {[0, 1, 2, 3].map((item) => (
      <Skeleton
        key={item}
        sx={{ borderRadius: "7px", mb: 1.5 }}
        variant="rectangular"
        height={94}
      />
    ))}
  </Box>
);

const DetailValue = ({ label, value, wide = false }) => (
  <Box sx={{ gridColumn: wide ? "1 / -1" : "auto", minWidth: 0 }}>
    <Typography
      color="#60758a"
      fontSize={10}
      fontWeight={800}
      letterSpacing="0.04em"
      textTransform="uppercase"
    >
      {label}
    </Typography>
    <Typography
      color="#10263d"
      fontSize={13}
      mt={0.35}
      sx={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}
    >
      {String(value || "").trim() || "—"}
    </Typography>
  </Box>
);

const ProcessDetail = ({ canManage, onClose, onEdit, onOpenTools, process }) => {
  const categoryTheme = getCategoryTheme(process.categorie_name);

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid #cbd8e6",
        borderRadius: "12px",
        mt: 2,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          alignItems: { xs: "flex-start", sm: "center" },
          backgroundColor: "#edf3f8",
          borderBottom: "1px solid #cbd8e6",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1.25,
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
        }}
      >
        <Typography color="#173d61" fontSize={15} fontWeight={800}>
          {process.instance || "—"} — {process.referentiel_nom || "—"}
        </Typography>
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            gap: 0.75,
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {canManage && <Button
            onClick={onEdit}
            startIcon={<EditOutlinedIcon />}
            sx={{ color: "#10263d", textTransform: "none" }}
          >
            Modifier
          </Button>}
          <Button
            onClick={onOpenTools}
            startIcon={<BuildOutlinedIcon />}
            sx={{
              backgroundColor: "#d5ad3f",
              color: "#10263d",
              fontWeight: 700,
              textTransform: "none",
              "&:hover": { backgroundColor: "#c39a2f" },
            }}
            variant="contained"
          >
            Outils
          </Button>
          <IconButton
            aria-label="Fermer le détail du processus"
            onClick={onClose}
            size="small"
            sx={{ color: "#10263d" }}
          >
            <CloseOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
          },
          p: 2,
        }}
      >
        <DetailValue label="Identifiant" value={process.instance} />
        <Box>
          <Typography
            color="#60758a"
            fontSize={10}
            fontWeight={800}
            letterSpacing="0.04em"
            textTransform="uppercase"
          >
            Catégorie
          </Typography>
          <Box
            component="span"
            sx={{
              backgroundColor: categoryTheme.color + "16",
              borderRadius: "999px",
              color: categoryTheme.color,
              display: "inline-block",
              fontSize: 10,
              fontWeight: 700,
              mt: 0.45,
              px: 1,
              py: 0.4,
            }}
          >
            {process.categorie_name || "—"}
          </Box>
        </Box>
        <DetailValue
          label="Direction"
          value={[process.direction_code, process.direction_name]
            .filter(Boolean)
            .join(" — ")}
        />
        <DetailValue label="Pilote" value={process.pilote_name} />
        <DetailValue label="Leader SMQ" value={process.leader_smq_name} />
        <DetailValue label="Mission" value={process.mission} wide />
        <DetailValue label="Entrées" value={process.entrant} />
        <DetailValue label="Activités" value={process.activite} />
        <DetailValue label="Sorties" value={process.sortant} />
        {String(process.ressource || "").trim() && (
          <DetailValue label="Ressources" value={process.ressource} />
        )}
        {String(process.indicateur || "").trim() && (
          <DetailValue label="Indicateurs" value={process.indicateur} />
        )}
        {String(process.valeur || "").trim() && (
          <DetailValue label="Valeurs" value={process.valeur} />
        )}
      </Box>
    </Paper>
  );
};

const Cartographie = () => {
  const { isAdmin: isSmqAdmin } = useSmqPermissions();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProcessId, setSelectedProcessId] = useState(null);
  const [processDialog, setProcessDialog] = useState({
    mode: "create",
    open: false,
    referentielCategory: null,
    values: null,
  });
  const [notification, setNotification] = useState({
    message: "",
    open: false,
    severity: "success",
  });
  const requestIdRef = useRef(0);

  const selectedProcess = useMemo(() => {
    for (const category of categories) {
      const process = (category.processus || []).find(
        (item) => String(item.processus_id) === String(selectedProcessId)
      );
      if (process) return process;
    }
    return null;
  }, [categories, selectedProcessId]);

  const loadCartographie = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setError(null);
    setLoading(true);

    try {
      const payload = await ProcessusService.getCartographieProcessus();

      if (!Array.isArray(payload?.data)) {
        throw new Error(
          "Le service de cartographie a retourné une réponse invalide"
        );
      }

      if (requestId === requestIdRef.current) {
        const nextCategories = sortCategories(payload.data);
        setCategories(nextCategories);
        setSelectedProcessId((currentId) => {
          if (!currentId) return null;
          const stillExists = nextCategories.some((category) =>
            (category.processus || []).some(
              (item) => String(item.processus_id) === String(currentId)
            )
          );
          return stillExists ? currentId : null;
        });
      }
    } catch (requestError) {
      if (requestId === requestIdRef.current) {
        setError(
          requestError?.message ||
            "Impossible de charger la cartographie des processus"
        );
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadCartographie();

    return () => {
      requestIdRef.current += 1;
    };
  }, [loadCartographie]);

  const openEditDialog = () => {
    if (isSmqAdmin && selectedProcess) {
      setProcessDialog({
        mode: "edit",
        open: true,
        referentielCategory: null,
        values: { ...selectedProcess },
      });
    }
  };

  const openCreateDialog = (category) => {
    if (!isSmqAdmin || processDialog.open) {
      return;
    }

    setProcessDialog({
      mode: "create",
      open: true,
      referentielCategory: {
        id: category.categorie_id,
        name: category.categorie_name,
      },
      values: null,
    });
  };

  const closeProcessDialog = () => {
    setProcessDialog((current) => ({ ...current, open: false }));
  };

  const handleProcessSaved = async () => {
    setNotification({
      message:
        processDialog.mode === "edit"
          ? "Processus modifié avec succès."
          : "Processus créé avec succès.",
      open: true,
      severity: "success",
    });
    await loadCartographie();
  };

  return (
    <SMQMainContainer>
      <>
        <HeaderProcessus
          icon={
            <MapOutlinedIcon
              aria-hidden="true"
              sx={{ color: "#d4a733", fontSize: 16 }}
            />
          }
          title="Cartographie"
        />

        <Box sx={{ pt: 3, pb: 3 }}>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: "#edf2f8",
              border: "1px solid #cbd8e6",
              borderRadius: "7px",
              p: { xs: 1.25, sm: 1.5 },
            }}
          >
            <Typography
              component="h1"
              sx={{
                color: "#10263d",
                fontFamily: "Georgia, 'Times New Roman', serif",
                fontSize: 14,
                fontWeight: 700,
                lineHeight: 1.25,
                mb: 1.25,
                textTransform: "uppercase",
              }}
            >
              Cartographie des processus — FID
            </Typography>

            {error && (
              <Alert
                action={
                  <Button color="inherit" onClick={loadCartographie} size="small">
                    Réessayer
                  </Button>
                }
                severity="error"
                sx={{ mb: 1.5 }}
              >
                Il y a eu un problème lors du chargement de la cartographie des processus.
              </Alert>
            )}

            <Box
              sx={{
                display: "grid",
                gap: { xs: 0, md: 1 },
                gridTemplateColumns: { xs: "1fr", md: "30px 1fr 30px" },
              }}
            >
              <SideLabel placement="bottom" reverse>
                Besoins et attentes des parties intéressées
              </SideLabel>

              <Box sx={{ minWidth: 0 }}>
                {loading ? (
                  <LoadingCartography />
                ) : categories.length === 0 ? (
                  <Box
                    sx={{
                      border: "1px dashed #9fb1c3",
                      borderRadius: "7px",
                      color: "#60758a",
                      p: 4,
                      textAlign: "center",
                    }}
                  >
                    <Typography fontSize={13} fontWeight={600}>
                      Aucune catégorie de processus disponible.
                    </Typography>
                  </Box>
                ) : (
                  categories.map((category, index) => {
                    const categoryKey =
                      (category.categorie_ordre ?? "sans-ordre") +
                      "-" +
                      (category.categorie_name || index);
                    const hasNextCategory = index < categories.length - 1;
                    const description = String(
                      category.categorie_description || ""
                    ).trim();
                    const isBidirectionalConnector =
                      normalizeText(category.categorie_name).includes(
                        "controle"
                      ) ||
                      normalizeText(description).includes("transversal");

                    return (
                      <React.Fragment key={categoryKey}>
                        <CategoryCard
                          addDisabled={processDialog.open}
                          canManage={isSmqAdmin}
                          category={category}
                          onAdd={openCreateDialog}
                          onSelect={setSelectedProcessId}
                          selectedProcessId={selectedProcessId}
                        />
                        {hasNextCategory && description && (
                          <Typography
                            sx={{
                              alignItems: "center",
                              color: "#60758a",
                              display: "flex",
                              fontSize: 10,
                              justifyContent: "center",
                              lineHeight: 1.4,
                              minHeight: 24,
                              textAlign: "center",
                            }}
                          >
                            {isBidirectionalConnector ? "↕" : "↓"} {description}{" "}
                            {isBidirectionalConnector ? "↕" : "↓"}
                          </Typography>
                        )}
                        {hasNextCategory && !description && (
                          <Box aria-hidden="true" sx={{ height: 12 }} />
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </Box>

              <SideLabel>
                Satisfaction des parties intéressées
              </SideLabel>
            </Box>
          </Paper>

          {selectedProcess && (
            <ProcessDetail
              canManage={isSmqAdmin}
              onClose={() => setSelectedProcessId(null)}
              onEdit={openEditDialog}
              onOpenTools={() =>
                navigate(
                  `/smq_nc/processus/outils/${selectedProcess.processus_id}`
                )
              }
              process={selectedProcess}
            />
          )}
        </Box>

        <ProcessusAdd
          initialValues={processDialog.values}
          mode={processDialog.mode}
          onClose={closeProcessDialog}
          onSaved={handleProcessSaved}
          open={processDialog.open}
          referentielCategory={processDialog.referentielCategory}
        />

        <Snackbar
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          autoHideDuration={4000}
          onClose={() =>
            setNotification((current) => ({ ...current, open: false }))
          }
          open={notification.open}
        >
          <Alert
            elevation={6}
            onClose={() =>
              setNotification((current) => ({ ...current, open: false }))
            }
            severity={notification.severity}
            variant="filled"
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </>
    </SMQMainContainer>
  );
};

export default Cartographie;
